import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  useUndo,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { errorMessage } from '@/lib/errors';
import {
  useCategoryMutations,
  useProductCategories,
  useProductSearch,
  type ProductSearchRow,
} from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * מחלקות המוצרים (מיגרציה 089).
 *
 * לא מסך הרשימות הגנרי, כי כאן יש כיוון שני: פותחים מחלקה
 * ומכניסים אליה מוצרים. זו הדרך שבה באמת בונים מחלקה — דרך טופס
 * המוצר זה עשרים פתיחות של מגש.
 */
export function CategoriesPage() {
  const undo = useUndo();
  const canManage = usePerm('products.manage');
  const categories = useProductCategories();
  const { create, rename, remove } = useCategoryMutations();

  const [name, setName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action().catch((caught: unknown) => setError(errorMessage(caught)));
  };

  const rows = (categories.data ?? []).filter((category) => !undo.isPending(category.id));

  return (
    <AdminPage
      title="מחלקות"
      subtitle="לפיהן מסודר הקטלוג. מוצר יכול לשבת בכמה מחלקות"
      backTo="/admin/products"
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const value = name.trim();
          if (!value) return;
          run(async () => {
            await create.mutateAsync(value);
            setName('');
          });
        }}
      >
        <div className="grid gap-1">
          <label htmlFor="new-category" className="text-body-sm text-muted">
            שם המחלקה
          </label>
          <input
            id="new-category"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={`${FIELD} w-64 max-w-full`}
          />
        </div>
        <Button variant="primary" type="submit" disabled={create.isPending}>
          <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
          הוספת מחלקה
        </Button>
      </form>

      {categories.isPending && <Skeleton className="h-32" />}
      {categories.isError && <ErrorState onRetry={() => void categories.refetch()} />}
      {categories.data?.length === 0 && (
        <EmptyState
          title="אין עדיין מחלקות"
          description="מחלקה ראשונה — מכירות, מרלוג, מטה — ואפשר להתחיל לשייך אליה מוצרים."
        />
      )}

      <ul className="grid list-none gap-3 p-0">
        {rows.map((category) => (
          <li key={category.id} className="grid gap-3 rounded-md border border-line bg-raised p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              {renamingId === category.id ? (
                <form
                  className="flex flex-wrap items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const value = renameValue.trim();
                    if (!value) return;
                    run(async () => {
                      await rename.mutateAsync({ id: category.id, name: value });
                      setRenamingId(null);
                    });
                  }}
                >
                  <label htmlFor={`rename-${category.id}`} className="sr-only">
                    שם המחלקה
                  </label>
                  <input
                    id={`rename-${category.id}`}
                    value={renameValue}
                    onChange={(event) => setRenameValue(event.target.value)}
                    className={`${FIELD} w-56`}
                  />
                  <Button variant="primary" type="submit">
                    <Check size={18} strokeWidth={1.75} aria-hidden="true" />
                    שמירת השם
                  </Button>
                  <Button variant="ghost" onClick={() => setRenamingId(null)}>
                    <X size={18} strokeWidth={1.75} aria-hidden="true" />
                    ביטול
                  </Button>
                </form>
              ) : (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-title font-medium">{category.name}</span>
                  <span className="text-body-sm text-muted">
                    {category.product_count === 1
                      ? 'מוצר אחד'
                      : `${category.product_count} מוצרים`}
                  </span>
                </span>
              )}

              <div className="flex items-center gap-1">
                <Button
                  variant="secondary"
                  onClick={() => setOpenId(openId === category.id ? null : category.id)}
                >
                  {openId === category.id ? 'סגירה' : 'שיוך מוצרים'}
                </Button>
                <Button
                  variant="ghost"
                  aria-label={`שינוי שם ל${category.name}`}
                  onClick={() => {
                    setRenamingId(category.id);
                    setRenameValue(category.name);
                  }}
                >
                  <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
                </Button>
                <Button
                  variant="ghost"
                  aria-label={`מחיקת ${category.name}`}
                  onClick={() =>
                    undo.remove({
                      id: category.id,
                      message: `המחלקה "${category.name}" נמחקה`,
                      perform: () => remove.mutateAsync(category.id),
                    })
                  }
                >
                  <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                </Button>
              </div>
            </div>

            {openId === category.id && (
              <CategoryProducts categoryId={category.id} onError={setError} />
            )}
          </li>
        ))}
      </ul>
    </AdminPage>
  );
}

/**
 * שיוך מוצרים למחלקה אחת.
 *
 * שתי רשימות ולא אחת: מה שכבר במחלקה, ומה שהחיפוש מצא. רשימה
 * אחת עם סימוני וי הייתה מציגה עמוד אחד מתוך אלפיים, ומי שכבר
 * במחלקה היה נעלם ברגע שמקלידים משהו.
 */
function CategoryProducts({
  categoryId,
  onError,
}: {
  categoryId: string;
  onError: (message: string | null) => void;
}) {
  const [term, setTerm] = useState('');
  const { setProducts } = useCategoryMutations();

  const inside = useProductSearch({
    term: '',
    categoryId,
    includeInactive: true,
    page: 0,
  });
  const found = useProductSearch({
    term,
    categoryId: null,
    includeInactive: true,
    page: 0,
  });

  const insideIds = new Set((inside.data?.rows ?? []).map((row) => row.id));

  const change = (input: { add?: string[]; remove?: string[] }) => {
    onError(null);
    void setProducts
      .mutateAsync({ categoryId, ...input })
      .catch((caught: unknown) => onError(errorMessage(caught)));
  };

  return (
    <Panel>
      <div className="grid gap-3">
        <div className="grid gap-2">
          <span className="text-label font-medium">
            במחלקה ({inside.data?.total ?? 0})
          </span>
          {inside.isPending && <Skeleton className="h-10" />}
          {inside.data?.total === 0 && (
            <p className="m-0 text-body-sm text-muted">עוד אין מוצרים במחלקה הזו.</p>
          )}
          <ul className="flex list-none flex-wrap gap-2 p-0">
            {(inside.data?.rows ?? []).map((product) => (
              <li key={product.id}>
                <button
                  type="button"
                  aria-label={`הסרת ${product.name} מהמחלקה`}
                  onClick={() => change({ remove: [product.id] })}
                  className="flex min-h-11 cursor-pointer items-center gap-1 rounded-pill border border-olive bg-olive-soft px-3 text-label text-ink"
                >
                  {product.name}
                  <X size={14} strokeWidth={1.75} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          {(inside.data?.total ?? 0) > (inside.data?.rows.length ?? 0) && (
            <p className="m-0 text-body-sm text-muted">
              מוצגים {inside.data?.rows.length} הראשונים. אפשר לחפש מוצר כדי להסיר אותו.
            </p>
          )}
        </div>

        <div className="grid gap-2">
          <label htmlFor={`search-${categoryId}`} className="text-label font-medium">
            הוספת מוצרים
          </label>
          <div className="relative">
            <Search
              size={20}
              strokeWidth={1.75}
              aria-hidden="true"
              className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted"
            />
            <input
              id={`search-${categoryId}`}
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="חיפוש בקטלוג"
              className={`${FIELD} ps-10`}
            />
          </div>

          {term.trim() === '' ? (
            <p className="m-0 text-body-sm text-muted">
              הקטלוג גדול מכדי להציג אותו כולו. חיפוש יביא את המוצרים להוספה.
            </p>
          ) : (
            <ul className="grid max-h-64 list-none gap-1 overflow-y-auto p-0">
              {(found.data?.rows ?? []).map((product: ProductSearchRow) => (
                <li key={product.id}>
                  <button
                    type="button"
                    disabled={insideIds.has(product.id)}
                    onClick={() => change({ add: [product.id] })}
                    className="flex min-h-11 w-full cursor-pointer items-center justify-between gap-2 rounded-sm border-0 bg-transparent px-3 text-start text-body text-ink hover:bg-olive-soft disabled:cursor-default disabled:text-muted"
                  >
                    <span>
                      {product.name}
                      {product.sku && (
                        <span className="text-body-sm text-muted"> · {product.sku}</span>
                      )}
                    </span>
                    <span className="text-label text-muted">
                      {insideIds.has(product.id) ? 'כבר במחלקה' : 'הוספה'}
                    </span>
                  </button>
                </li>
              ))}
              {found.data?.total === 0 && (
                <li className="text-body-sm text-muted">אין תוצאה ל"{term}".</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </Panel>
  );
}
